import { tracker } from '@internal/utils/tracking/tracker';
import { ClickEventTargetName } from '@internal/utils/tracking/TTrackingEvent';
import {
    ArrowForward,
    KeyboardArrowDown,
    VerifiedUser,
} from '@mui/icons-material';
import { getMarkdownComponents } from '@renderer-ui-library/atoms/Text';
import {
    ActionableButton,
    DynamicYoutubeVideo,
} from '@renderer-ui-library/molecules';
import { Media } from '@renderer-ui-library/molecules/Media/Media';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    SvgIcon,
    Typography,
} from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Service, ServiceTypes } from '../IMachineOverviewBlockData';
import styles from './usps.module.scss';

const clickEvents: Record<ServiceTypes, ClickEventTargetName> = {
    [ServiceTypes.Inspection]: 'usps.inspection',
    [ServiceTypes.Warranty]: 'usps.warranty',
    [ServiceTypes.Transport]: 'usps.transport',
    [ServiceTypes.Payment]: 'usps.payment',
    [ServiceTypes.Financing]: 'usps.financing',
    [ServiceTypes.Customers]: 'usps.customers',
    [ServiceTypes.PartnerDealers]: 'usps.partner_dealers',
    [ServiceTypes.Homologation]: 'usps.homologation',
};

type Props = {
    services: Service[];
};

export const USPs: React.FC<Props> = ({ services }) => {
    const [expanded, setExpanded] = useState<number | false>(-1);
    const handleChange = useCallback(
        (index: number) =>
            (event: React.SyntheticEvent, newExpanded: boolean) => {
                setExpanded(newExpanded ? index : -1);
                const clickEvent = clickEvents[services[index].serviceType];
                if (clickEvent) {
                    tracker.trackEvent({
                        name: 'click',
                        target_name: clickEvent,
                        is_interaction: true,
                    });
                }
            },
        [services],
    );
    const markdownComponents = useMemo(() => getMarkdownComponents(), []);
    const { isMobile } = useMediaQuery();

    return (
        <div className={styles.uspWrapper}>
            {services.map((service, index) => {
                return (
                    <Accordion
                        key={service.serviceType}
                        className={styles.accordion}
                        elevation={0}
                        disableGutters
                        expanded={expanded === index}
                        onChange={handleChange(index)}
                    >
                        <AccordionSummary
                            expandIcon={<KeyboardArrowDown color="primary" />}
                        >
                            <SvgIcon
                                component={VerifiedUser}
                                className={styles.uspIcon}
                            />
                            <Typography
                                variant="h4"
                                className={styles.headline}
                            >
                                {service.headline}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={styles.accordionDetails}>
                            {service.media?.videoUrl && (
                                <div className={styles.videoWrapper}>
                                    <DynamicYoutubeVideo
                                        url={service.media?.videoUrl}
                                        mediaId={service.media?.videoUrl}
                                        autoPlayVideo={false}
                                    />
                                </div>
                            )}
                            {service.media?.files.data &&
                                service.media.files.data.length > 0 && (
                                    <div className={styles.mediaWrapper}>
                                        <Media
                                            media={service.media}
                                            ratio={32 / 9}
                                        />
                                    </div>
                                )}
                            <ReactMarkdown
                                components={markdownComponents}
                                rehypePlugins={[rehypeRaw]}
                                className={styles.description}
                            >
                                {service.description}
                            </ReactMarkdown>
                            {service.button && (
                                <div className={styles.cta}>
                                    <ActionableButton
                                        fullWidth={isMobile}
                                        noMinWidth={!isMobile}
                                        button={service.button}
                                        variant="outlined"
                                        color="success"
                                        endIcon={<ArrowForward />}
                                    />
                                </div>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </div>
    );
};

USPs.displayName = 'USPs';
